const { MODAL_NAMES } = require('@/vue/modals/MODAL_NAMES');

/**
 * Provides methods to display generic re-usable "dialogs". Some replace the browser's built in functionality
 * with more style (confirm, prompt). Some add additional abilities (numPad).
 *
 * @constructor
 *
 * @param $modal
 * @param {VueModalFactory} vueModalFactory
 */
const DialogService = function (
    $modal,
    vueModalFactory
) {
    this.$modal = $modal;
    this.vueModalFactory = vueModalFactory;
};

DialogService.prototype = {
    /**
     * @param {string|string[]|null} textLangKeys Key(s) from the lang file to display as the message in the modal.
     *                                            This might be null if you're going to use custom text in props.
     * @param {object} [props] Object of props that will be passed to the Vue modal.
     * @param {function} [onOpen]
     *
     * @return {Promise}
     */
    alert(
        textLangKeys,
        props = {},
        onOpen = null
    ) {
        return this.vueModalFactory.open(
            MODAL_NAMES.ALERT,
            {
                textLangKeys,
                ...props,
            },
            onOpen
        );
    },

    /**
     * @param {string|string[]|null} textLangKeys Key(s) from the lang file to display as the message in the modal.
     *                                            This might be null if you're going to use custom text in props.
     * @param {object} [options] Object of options to pass to the modal.
     */
    prompt: function prompt(
        textLangKeys,
        options
    ) {
        return this.vueModalFactory.open(
            MODAL_NAMES.PROMPT,
            {
                textLangKeys,
                ...options,
            }
        );
    },

    /**
     * @param {string|string[]|null} textLangKeys Key(s) from the lang file to display as the message in the modal.
     *                                            This might be null if you're going to use custom text in props.
     * @param {object} [props] Object of props that will be passed to the Vue modal.
     * @param {function} [onOpen] A function to be called once the modal is visible.
     *
     * @return {Promise<{value: number|null}>}
     */
    numPad(
        textLangKeys,
        props = {},
        onOpen = null
    ) {
        return this.vueModalFactory.open(
            MODAL_NAMES.NUM_PAD,
            {
                textLangKeys,
                ...props,
            },
            onOpen
        );
    },

    /**
     * Poses a question to the user with No/Yes buttons.
     * Returns a promise with a boolean result. true if the user clicked "yes", false if the user clicked "no".
     * If you want to provide custom text (not a lang file key) use an empty string for textLangKeys, and provide the
     * custom text as `customText` in the `props` object.
     * Or for custom HTML do the same but use `customHtml` in the `props` object.
     * See src/vue/mixins/TextPropsMixin.js
     *
     * @param {string|string[]|null} textLangKeys Key(s) from the lang file to display as the message in the modal.
     *                                            This might be null if you're going to use custom text in props.
     * @param {ConfirmModalProps} [props] Object of props that will be passed to the Vue modal.
     * @param {function} [onOpen] A function to be called once the modal is visible.
     *
     * @return {Promise<boolean>}
     */
    confirm(
        textLangKeys,
        props = {},
        onOpen = null
    ) {
        return this.vueModalFactory.open(
            MODAL_NAMES.CONFIRM,
            {
                textLangKeys,
                ...props,
            },
            onOpen
        );
    },

    /**
     * Like confirm, but you can supply your own custom buttons on the right
     * of the dialog and you get a cancel button on the left.
     * See src/vue/modals/Dialogs/CustomConfirmModal.vue
     *
     * @param {string|string[]|null} textLangKeys Key(s) from the lang file to display as the message in the modal.
     *                                            This might be null if you're going to use custom text in props.
     * @param {object[]} btns
     * @param {object} [props]
     * @param {function} [onOpen]
     *
     * @return {Promise}
     */
    customConfirm(
        textLangKeys,
        btns,
        props = {},
        onOpen = null
    ) {
        return this.vueModalFactory.open(
            MODAL_NAMES.CUSTOM_CONFIRM,
            {
                textLangKeys,
                btns,
                ...props,
            },
            onOpen
        );
    },
};

module.exports = DialogService;
